/**
 * Frontend file caching service using IndexedDB
 * Caches actual file content (images/videos) for instant display
 * Uses apiClient for authenticated requests with proper token handling
 * Supports both OAuth and approval token authentication
 */
import { apiClient } from "@/services/apiClient";

export interface CachedFile {
  fileId: string;
  url: string;
  blob: Blob;
  mimeType: string;
  size: number;
  cachedAt: number;
  lastAccessed: number;
  expiresAt: number;
  clientId: string;
}

export interface CacheStats {
  totalSize: number;
  totalFiles: number;
  hitRate: number;
  oldestFile: number;
  newestFile: number;
}

export interface CacheConfig {
  maxSizeBytes: number;
  maxAge: number;
  maxFiles: number;
  preloadBatchSize: number;
  preloadDelay: number;
  dbName: string;
  dbVersion: number;
}

const DEFAULT_CONFIG: CacheConfig = {
  maxSizeBytes: 500 * 1024 * 1024, // 500MB
  maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
  maxFiles: 1000,
  preloadBatchSize: 5,
  preloadDelay: 100, // ms between batches
  dbName: "MediaFileCache",
  dbVersion: 1,
};

// Maximum file size for blob URL creation (10MB)
// Files larger than this will use direct authenticated URLs to avoid WebKit blob issues
const MAX_BLOB_SIZE = 10 * 1024 * 1024;

/**
 * Check if user is authenticated via either OAuth or approval tokens
 * @param clientId - Client ID for approval token lookup (optional)
 */
function isAuthenticated(clientId?: string): boolean {
  // Check OAuth authentication first
  if (apiClient.isAuthenticated()) {
    return true;
  }

  // Check approval token authentication if clientId provided
  if (clientId) {
    const approvalToken = localStorage.getItem(`approval_token_${clientId}`);
    if (approvalToken) {
      return true;
    }
  }

  return false;
}

class FileCacheService {
  private db: IDBDatabase | null = null;
  private config: CacheConfig;
  private initPromise: Promise<void> | null = null;
  private stats = {
    requests: 0,
    hits: 0,
    misses: 0,
  };

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Get current access token
   */
  private getAccessToken(): string | null {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user.accessToken || null;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Convert URL to use clean endpoint without query parameters
   * This allows apiClient to handle authentication properly
   */
  private cleanUrl(url: string): string {
    // Remove any existing access_token parameters from legacy URLs
    let cleanUrl = url;
    cleanUrl = cleanUrl.replace(/[?&]access_token=[^&]*&?/g, "");
    cleanUrl = cleanUrl.replace(/[?&]$/, "");
    return cleanUrl;
  }

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.dbName, this.config.dbVersion);

      request.onerror = () => {
        console.error("Failed to open IndexedDB:", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store for cached files
        if (!db.objectStoreNames.contains("files")) {
          const store = db.createObjectStore("files", { keyPath: "fileId" });
          store.createIndex("clientId", "clientId", { unique: false });
          store.createIndex("lastAccessed", "lastAccessed", { unique: false });
          store.createIndex("cachedAt", "cachedAt", { unique: false });
          store.createIndex("expiresAt", "expiresAt", { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Get cached file as blob URL
   */
  async getCachedFile(fileId: string): Promise<string | null> {
    await this.init();
    if (!this.db) return null;

    this.stats.requests++;

    try {
      const transaction = this.db.transaction(["files"], "readwrite");
      const store = transaction.objectStore("files");
      const request = store.get(fileId);

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const cachedFile = request.result as CachedFile | undefined;

          if (!cachedFile) {
            this.stats.misses++;
            resolve(null);
            return;
          }

          // Check if expired
          if (Date.now() > cachedFile.expiresAt) {
            store.delete(fileId);
            this.stats.misses++;
            resolve(null);
            return;
          }

          // Update last accessed time
          cachedFile.lastAccessed = Date.now();
          store.put(cachedFile);

          // Return the cached signed URL directly
          this.stats.hits++;
          resolve(cachedFile.url);
        };

        request.onerror = () => {
          this.stats.misses++;
          resolve(null);
        };
      });
    } catch (error) {
      console.error("Error getting cached file:", error);
      this.stats.misses++;
      return null;
    }
  }

  /**
   * Cache a file by requesting signed URL from backend
   * Instead of downloading the file, we store the signed URL directly
   */
  async cacheFile(
    url: string,
    fileId: string,
    clientId: string,
  ): Promise<string> {
    await this.init();

    try {
      // Clean URL to remove query parameters
      const cleanedUrl = this.cleanUrl(url);

      // Extract endpoint path from full URL for apiClient
      let endpoint = cleanedUrl;
      if (cleanedUrl.includes("://")) {
        const urlObj = new URL(cleanedUrl);
        endpoint = urlObj.pathname;
      }

      // Verify authentication (either OAuth or approval)
      if (!isAuthenticated(clientId)) {
        throw new Error("Not authenticated");
      }

      // Request signed URL as JSON instead of downloading blob
      try {
        // Check if we need to add approval token headers
        const additionalHeaders: Record<string, string> = {
          "Accept": "application/json",
        };

        // If not OAuth authenticated, try the approval access token.
        if (!apiClient.isAuthenticated() && clientId) {
          const approvalToken = localStorage.getItem(
            `approval_token_${clientId}`,
          );
          if (approvalToken) {
            additionalHeaders["X-Approval-Token"] = approvalToken;
          }
        }

        // Request signed URL as JSON (backend supports format=json or Accept: application/json)
        const response = await apiClient.get<{ url: string }>(
          `${endpoint}?format=json`,
          {
            timeout: 10000,
            headers: additionalHeaders,
          }
        );

        const signedUrl = response.url;
        const now = Date.now();

        // Store signed URL directly (no blob)
        const cachedFile = {
          fileId,
          url: signedUrl,
          blob: null as any, // Not used anymore
          mimeType: "image/webp", // Assume optimized format
          size: 0, // Not available without downloading
          cachedAt: now,
          lastAccessed: now,
          expiresAt: now + (50 * 60 * 1000), // 50 minutes (signed URLs valid for 60min)
          clientId,
        };

        // Store in IndexedDB
        if (this.db) {
          const transaction = this.db.transaction(["files"], "readwrite");
          const store = transaction.objectStore("files");
          await new Promise<void>((resolve, reject) => {
            const request = store.put(cachedFile);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          });
        }

        return signedUrl;
      } catch (error: any) {
        // Silent fallback - return original URL without logging spam
        return url;
      }
    } catch (error) {
      // Silent fallback - return original URL without logging spam
      return url;
    }
  }

  /**
   * Check if file is cached
   */
  async isCached(fileId: string): Promise<boolean> {
    await this.init();
    if (!this.db) return false;

    try {
      const transaction = this.db.transaction(["files"], "readonly");
      const store = transaction.objectStore("files");
      const request = store.get(fileId);

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const cachedFile = request.result as CachedFile | undefined;
          const isCached = !!cachedFile && Date.now() <= cachedFile.expiresAt;
          resolve(isCached);
        };
        request.onerror = () => resolve(false);
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Preload multiple files in batches
   */
  async preloadFiles(
    fileUrls: Array<{ url: string; fileId: string; clientId: string }>,
  ): Promise<void> {
    if (fileUrls.length === 0) return;

    for (let i = 0; i < fileUrls.length; i += this.config.preloadBatchSize) {
      const batch = fileUrls.slice(i, i + this.config.preloadBatchSize);

      // Filter out already cached files
      const uncachedBatch = [];
      for (const file of batch) {
        if (!(await this.isCached(file.fileId))) {
          uncachedBatch.push(file);
        }
      }

      if (uncachedBatch.length > 0) {
        // Process batch in parallel
        await Promise.allSettled(
          uncachedBatch.map((file) =>
            this.cacheFile(file.url, file.fileId, file.clientId),
          ),
        );

        // Small delay between batches to avoid overwhelming the browser
        if (i + this.config.preloadBatchSize < fileUrls.length) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.config.preloadDelay),
          );
        }
      }
    }
  }

  /**
   * Clear expired files
   */
  async clearExpiredFiles(): Promise<number> {
    await this.init();
    if (!this.db) return 0;

    try {
      const transaction = this.db.transaction(["files"], "readwrite");
      const store = transaction.objectStore("files");
      const index = store.index("expiresAt");
      const range = IDBKeyRange.upperBound(Date.now());
      const request = index.openCursor(range);

      let deletedCount = 0;

      return new Promise((resolve) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve(deletedCount);
          }
        };

        request.onerror = () => resolve(0);
      });
    } catch (error) {
      console.error("Error clearing expired files:", error);
      return 0;
    }
  }

  /**
   * Enforce storage limits by removing least recently used files
   */
  private async enforceStorageLimit(): Promise<void> {
    const stats = await this.getCacheStats();

    if (
      stats.totalSize <= this.config.maxSizeBytes &&
      stats.totalFiles <= this.config.maxFiles
    ) {
      return;
    }

    await this.init();
    if (!this.db) return;

    try {
      const transaction = this.db.transaction(["files"], "readwrite");
      const store = transaction.objectStore("files");
      const index = store.index("lastAccessed");
      const request = index.openCursor(); // Oldest first

      let deletedSize = 0;
      let deletedCount = 0;
      const targetSize = this.config.maxSizeBytes * 0.8; // Clear to 80% of limit

      return new Promise((resolve) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;

          if (
            cursor &&
            (stats.totalSize - deletedSize > targetSize ||
              stats.totalFiles - deletedCount > this.config.maxFiles)
          ) {
            const file = cursor.value as CachedFile;
            deletedSize += file.size;
            deletedCount++;
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = () => resolve();
      });
    } catch (error) {
      console.error("Error enforcing storage limit:", error);
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<CacheStats> {
    await this.init();
    if (!this.db) {
      return {
        totalSize: 0,
        totalFiles: 0,
        hitRate: 0,
        oldestFile: 0,
        newestFile: 0,
      };
    }

    try {
      const transaction = this.db.transaction(["files"], "readonly");
      const store = transaction.objectStore("files");
      const request = store.getAll();

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const files = request.result as CachedFile[];

          const totalSize = files.reduce((sum, file) => sum + file.size, 0);
          const totalFiles = files.length;
          const hitRate =
            this.stats.requests > 0
              ? (this.stats.hits / this.stats.requests) * 100
              : 0;

          const timestamps = files.map((f) => f.cachedAt);
          const oldestFile =
            timestamps.length > 0 ? Math.min(...timestamps) : 0;
          const newestFile =
            timestamps.length > 0 ? Math.max(...timestamps) : 0;

          resolve({
            totalSize,
            totalFiles,
            hitRate: Math.round(hitRate * 100) / 100,
            oldestFile,
            newestFile,
          });
        };

        request.onerror = () =>
          resolve({
            totalSize: 0,
            totalFiles: 0,
            hitRate: 0,
            oldestFile: 0,
            newestFile: 0,
          });
      });
    } catch (error) {
      console.error("Error getting cache stats:", error);
      return {
        totalSize: 0,
        totalFiles: 0,
        hitRate: 0,
        oldestFile: 0,
        newestFile: 0,
      };
    }
  }

  /**
   * Invalidate a specific cached file by ID
   * Call this after uploading a revision to force re-fetch
   */
  async invalidateFile(fileId: string): Promise<boolean> {
    await this.init();
    if (!this.db) return false;

    try {
      const transaction = this.db.transaction(["files"], "readwrite");
      const store = transaction.objectStore("files");

      return new Promise((resolve) => {
        const request = store.delete(fileId);
        request.onsuccess = () => {
          console.log(`🗑️ Cache invalidated for file: ${fileId}`);
          resolve(true);
        };
        request.onerror = () => {
          console.warn(`Failed to invalidate cache for file: ${fileId}`);
          resolve(false);
        };
      });
    } catch (error) {
      console.error("Error invalidating cached file:", error);
      return false;
    }
  }

  /**
   * Clear all cached files for a specific client
   */
  async clearClientCache(clientId: string): Promise<number> {
    await this.init();
    if (!this.db) return 0;

    try {
      const transaction = this.db.transaction(["files"], "readwrite");
      const store = transaction.objectStore("files");
      const index = store.index("clientId");
      const request = index.openCursor(IDBKeyRange.only(clientId));

      let deletedCount = 0;

      return new Promise((resolve) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve(deletedCount);
          }
        };

        request.onerror = () => resolve(0);
      });
    } catch (error) {
      console.error("Error clearing client cache:", error);
      return 0;
    }
  }

  /**
   * Clear all cached files
   */
  async clearAllCache(): Promise<void> {
    await this.init();
    if (!this.db) return;

    try {
      const transaction = this.db.transaction(["files"], "readwrite");
      const store = transaction.objectStore("files");
      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => {
          this.stats = { requests: 0, hits: 0, misses: 0 };
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Error clearing all cache:", error);
    }
  }

  /**
   * Format bytes to human readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): CacheConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const fileCacheService = new FileCacheService();

